import { useState, useMemo, useRef } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useTabParams } from "@/contexts/TabParamsContext";
import { useTabLabel } from "@/hooks/useTabLabel";
import { useQuery } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Vendor, getComplianceStatus } from "@/hooks/useVendors";
import { WorkerComplianceTab } from "@/components/controller/WorkerComplianceTab";
import { useVendorNotes, useCreateVendorNote } from "@/hooks/useVendorNotes";
import { useVendorDocuments, useUploadVendorDocument, useDeleteVendorDocument, VendorDocument } from "@/hooks/useVendorDocuments";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { NewVendorDialog } from "@/components/controller/NewVendorDialog";
import { ArrowLeft, Pencil, Upload, Download, Trash2, FileText, ChevronRight, Receipt, FileSpreadsheet } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { toast } from "sonner";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const TYPE_LABELS: Record<string, string> = {
  subcontractor: "👷 Subcontractor",
  material_supplier: "🧱 Material Supplier",
  equipment_rental: "🏗 Equipment Rental",
  general: "📋 General",
};

const DOC_TYPES = [
  { value: "w9", label: "W9" },
  { value: "coi", label: "Certificate of Insurance" },
  { value: "lien_waiver_conditional", label: "Lien Waiver (Conditional)" },
  { value: "lien_waiver_unconditional", label: "Lien Waiver (Unconditional)" },
  { value: "license", label: "License" },
  { value: "contract", label: "Contract" },
  { value: "other", label: "Other" },
];

const VendorDetail = () => {
  const { id } = useTabParams<{ id: string }>();
  const navigate = useTabNavigate();
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [uploadDocType, setUploadDocType] = useState("w9");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: vendor, isLoading } = useQuery({
    queryKey: ["vendor", id],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("vendors") as any)
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data as Vendor | null;
    },
    enabled: !!user && !!id,
  });

  // Work orders for this vendor
  const { data: workOrders } = useQuery({
    queryKey: ["vendor_work_orders", id],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("work_orders") as any)
        .select("*, projects(name)")
        .eq("vendor_id", id!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!user && !!id,
  });

  // POs for this vendor
  const { data: purchaseOrders } = useQuery({
    queryKey: ["vendor_pos", id],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("purchase_orders") as any)
        .select("*, projects(name)")
        .eq("vendor_id", id!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!user && !!id,
  });

  // Bills for this vendor
  const { data: bills } = useQuery({
    queryKey: ["vendor_bills", id],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("bills") as any)
        .select("*, projects(name)")
        .eq("vendor_id", id!)
        .order("bill_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!user && !!id,
  });

  // Standalone expenses for this vendor (exclude bill-derived rows to avoid double counting)
  const { data: expenses } = useQuery({
    queryKey: ["vendor_expenses", id],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("project_expenses") as any)
        .select("*, projects(name)")
        .eq("vendor_id", id!)
        .is("bill_id", null)
        .order("expense_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!user && !!id,
  });

  const { data: notes } = useVendorNotes(id);
  const { data: documents } = useVendorDocuments(id);
  const createNote = useCreateVendorNote();
  const uploadDocument = useUploadVendorDocument();
  const deleteDocument = useDeleteVendorDocument();

  useTabLabel(vendor?.name ? vendor.name : undefined);

  const compliance = vendor ? getComplianceStatus(vendor) : { level: "green" as const, issues: [] };

  // Bills count toward outlays only when actually paid (paid or partially_paid)
  const PAID_BILL_STATUSES = ["paid", "partially_paid"];

  const stats = useMemo(() => {
    const totalWOs = workOrders?.length || 0;
    const activeWOs = workOrders?.filter((w: any) => w.status === "active" || w.status === "in_progress").length || 0;
    const billsPaid = (bills ?? [])
      .filter((b: any) => PAID_BILL_STATUSES.includes(b.status))
      .reduce((s: number, b: any) => s + Number(b.amount_paid ?? b.total ?? 0), 0);
    const expensesPaid = (expenses ?? [])
      .filter((e: any) => e.status === "approved" || e.status === "reimbursed")
      .reduce((s: number, e: any) => s + Number(e.amount ?? 0), 0);
    const totalOutlays = billsPaid + expensesPaid;
    const retainageHeld = (bills ?? []).reduce((s: number, b: any) => s + Number(b.retainage_amount || 0), 0);
    return { totalWOs, activeWOs, totalOutlays, billsPaid, expensesPaid, retainageHeld };
  }, [workOrders, bills, expenses]);


  const handleAddNote = async () => {
    if (!noteText.trim() || !id) return;
    try {
      await createNote.mutateAsync({ vendor_id: id, content: noteText });
      toast.success("Note added");
      setNoteText("");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    try {
      await uploadDocument.mutateAsync({
        vendor_id: id,
        document_type: uploadDocType,
        file,
      });
      toast.success("Document uploaded");
    } catch (err: any) { toast.error(err.message); }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownload = async (doc: VendorDocument) => {
    const { data } = await supabase.storage.from("vendor-docs").createSignedUrl(doc.file_path, 60);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  const expiryBadge = (date: string | null) => {
    if (!date) return <Badge variant="destructive" className="text-[10px]">Missing</Badge>;
    const d = new Date(date);
    const days = differenceInDays(d, new Date());
    if (days < 0) return <Badge variant="destructive" className="text-[10px]">Expired</Badge>;
    if (days < 30) return <Badge variant="secondary" className="text-[10px]">Exp in {days}d</Badge>;
    return <Badge variant="outline" className="text-[10px]">Valid</Badge>;
  };

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (!vendor) return <p className="text-muted-foreground">Vendor not found.</p>;

  const types = vendor.vendor_types || [vendor.vendor_type || "general"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate("/controller/vendors")}>
            <ArrowLeft className="mr-1 h-4 w-4" />Back
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-1 h-3 w-3" />Edit
          </Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight">{vendor.name}</h1>
          {types.map((t: string) => (
            <Badge key={t} variant="secondary">{TYPE_LABELS[t] || t}</Badge>
          ))}
        </div>
        {/* Compliance bar */}
        <div className="flex flex-wrap gap-3 text-xs">
          <span>W9: {vendor.w9_on_file ? "✅ On File" : "❌ Missing"}</span>
          <span>License: {expiryBadge(vendor.license_expiry)}</span>
          <span>Insurance: {expiryBadge(vendor.insurance_expiry)}</span>
          <span>Lien Waiver: {vendor.lien_waiver_required ? "Required" : "Not Required"}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total WOs", value: stats.totalWOs },
          { label: "Active WOs", value: stats.activeWOs },
          { label: "Total Outlays", value: fmt(stats.totalOutlays) },
          { label: "Retainage Held", value: fmt(stats.retainageHeld) },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className="text-lg font-semibold">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="work_orders">
        <TabsList>
          <TabsTrigger value="work_orders">Work Orders</TabsTrigger>
          <TabsTrigger value="purchase_orders">Purchase Orders</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="payments">Outlays</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* WORK ORDERS */}
        <TabsContent value="work_orders">
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {workOrders?.map((wo: any) => (
              <Card key={wo.id}>
                <CardContent className="p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-medium">{wo.wo_number || "—"}</span>
                    <StatusBadge status={wo.status} />
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{wo.projects?.name || "—"}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground truncate max-w-[60%]">{wo.scope || wo.description || "—"}</span>
                    <span className="text-lg font-bold">{fmt(Number(wo.total || 0))}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {!workOrders?.length && <p className="text-center text-muted-foreground py-8">No work orders.</p>}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>WO #</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workOrders?.map((wo: any) => (
                  <TableRow key={wo.id}>
                    <TableCell className="font-medium">{wo.wo_number || "—"}</TableCell>
                    <TableCell>{wo.projects?.name || "—"}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{wo.scope || wo.description || "—"}</TableCell>
                    <TableCell className="text-right">{fmt(Number(wo.total || 0))}</TableCell>
                    <TableCell><StatusBadge status={wo.status} /></TableCell>
                  </TableRow>
                ))}
                {!workOrders?.length && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No work orders.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* PURCHASE ORDERS */}
        <TabsContent value="purchase_orders">
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {purchaseOrders?.map((po: any) => (
              <Card key={po.id}>
                <CardContent className="p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-medium">{po.number || "—"}</span>
                    <StatusBadge status={po.status} />
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{po.projects?.name || "—"}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground truncate max-w-[60%]">{po.description || "—"}</span>
                    <span className="text-lg font-bold">{fmt(Number(po.amount || 0))}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {!purchaseOrders?.length && <p className="text-center text-muted-foreground py-8">No purchase orders.</p>}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO #</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchaseOrders?.map((po: any) => (
                  <TableRow key={po.id}>
                    <TableCell className="font-medium">{po.number || "—"}</TableCell>
                    <TableCell>{po.projects?.name || "—"}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{po.description || "—"}</TableCell>
                    <TableCell className="text-right">{fmt(Number(po.amount || 0))}</TableCell>
                    <TableCell><StatusBadge status={po.status} /></TableCell>
                  </TableRow>
                ))}
                {!purchaseOrders?.length && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No purchase orders.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="space-y-4">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-3">
            <Select value={uploadDocType} onValueChange={setUploadDocType}>
              <SelectTrigger className="w-full md:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((dt) => (
                  <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
            <Button size="sm" className="w-full md:w-auto" onClick={() => fileInputRef.current?.click()} disabled={uploadDocument.isPending}>
              <Upload className="mr-1 h-4 w-4" />{uploadDocument.isPending ? "Uploading…" : "Upload"}
            </Button>
          </div>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {documents?.map((doc) => (
              <Card key={doc.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px]">
                      {DOC_TYPES.find((d) => d.value === doc.document_type)?.label || doc.document_type}
                    </Badge>
                    {doc.expiry_date ? expiryBadge(doc.expiry_date) : null}
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <FileText className="h-3 w-3" /><span className="truncate">{doc.file_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{doc.created_at ? format(new Date(doc.created_at), "MMM d, yyyy") : "—"}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}><Download className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteDocument.mutateAsync(doc).then(() => toast.success("Deleted"))}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {!documents?.length && <p className="text-center text-muted-foreground py-8">No documents uploaded.</p>}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>File Name</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents?.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {DOC_TYPES.find((d) => d.value === doc.document_type)?.label || doc.document_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="flex items-center gap-1">
                      <FileText className="h-3 w-3" />{doc.file_name}
                    </TableCell>
                    <TableCell>{doc.expiry_date ? expiryBadge(doc.expiry_date) : "—"}</TableCell>
                    <TableCell className="text-sm">{doc.created_at ? format(new Date(doc.created_at), "MMM d, yyyy") : "—"}</TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                        <Download className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteDocument.mutateAsync(doc).then(() => toast.success("Deleted"))}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!documents?.length && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No documents uploaded.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* OUTLAYS — unified bills + standalone expenses */}
        <TabsContent value="payments" className="space-y-4">
          {(() => {
            type Outlay = {
              id: string;
              kind: "bill" | "expense";
              date: string | null;
              ref: string;
              project: string;
              description: string;
              status: string;
              amount: number;
              source: string | null;
              meta: Record<string, any>;
            };

            const billRows: Outlay[] = (bills ?? []).map((b: any) => ({
              id: `bill-${b.id}`,
              kind: "bill",
              date: b.bill_date ?? b.created_at ?? null,
              ref: b.bill_number || "—",
              project: b.projects?.name || "—",
              description: b.description || "—",
              status: b.status || "—",
              amount: PAID_BILL_STATUSES.includes(b.status)
                ? Number(b.amount_paid ?? b.total ?? 0)
                : Number(b.total ?? 0),
              source: b.source ?? null,
              meta: {
                "Bill Total": fmt(Number(b.total ?? 0)),
                "Subtotal": fmt(Number(b.subtotal ?? 0)),
                "Retainage": fmt(Number(b.retainage_amount ?? 0)),
                "Amount Paid": fmt(Number(b.amount_paid ?? 0)),
                "Due Date": b.due_date ? format(new Date(b.due_date), "MMM d, yyyy") : "—",
                "Paid On": b.paid_at ? format(new Date(b.paid_at), "MMM d, yyyy") : "—",
                "Payment Ref": b.payment_reference || "—",
                "Description": b.description || "—",
              },
            }));

            const expenseRows: Outlay[] = (expenses ?? []).map((e: any) => ({
              id: `exp-${e.id}`,
              kind: "expense",
              date: e.expense_date ?? e.created_at ?? null,
              ref: e.qb_expense_id ? `QB-${e.qb_expense_id}` : "Expense",
              project: e.projects?.name || "—",
              description: e.description || "—",
              status: e.status || "—",
              amount: Number(e.amount ?? 0),
              source: e.qb_expense_id ? "quickbooks" : (e.source_type ?? null),
              meta: {
                "Description": e.description || "—",
                "Payment Method": e.payment_method || "—",
                "Notes": e.notes || "—",
                "Vendor Name": e.vendor_name || "—",
              },
            }));

            const all = [...billRows, ...expenseRows].sort((a, b) => {
              const da = a.date ? new Date(a.date).getTime() : 0;
              const db = b.date ? new Date(b.date).getTime() : 0;
              return db - da;
            });

            const totalAll = all.reduce((s, r) => s + r.amount, 0);

            if (all.length === 0) {
              return <p className="text-center text-muted-foreground py-8">No bills or expenses recorded for this vendor.</p>;
            }

            return (
              <>
                {/* Summary header */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card><CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Total Outlays</p>
                    <p className="font-semibold text-lg">{fmt(totalAll)}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Bills</p>
                    <p className="font-semibold text-lg">{fmt(billRows.reduce((s, r) => s + r.amount, 0))}</p>
                    <p className="text-[10px] text-muted-foreground">{billRows.length} record{billRows.length !== 1 ? "s" : ""}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Expenses</p>
                    <p className="font-semibold text-lg">{fmt(expenseRows.reduce((s, r) => s + r.amount, 0))}</p>
                    <p className="text-[10px] text-muted-foreground">{expenseRows.length} record{expenseRows.length !== 1 ? "s" : ""}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Paid (Bills)</p>
                    <p className="font-semibold text-lg">{fmt(stats.billsPaid)}</p>
                  </CardContent></Card>
                </div>

                {/* Unified expandable list */}
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Project</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {all.map((row) => (
                        <Collapsible key={row.id} asChild>
                          <>
                            <CollapsibleTrigger asChild>
                              <TableRow className="cursor-pointer hover:bg-muted/50 group">
                                <TableCell>
                                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
                                </TableCell>
                                <TableCell className="text-sm">
                                  {row.date ? format(new Date(row.date), "MMM d, yyyy") : "—"}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    {row.kind === "bill" ? (
                                      <FileSpreadsheet className="h-3.5 w-3.5 text-muted-foreground" />
                                    ) : (
                                      <Receipt className="h-3.5 w-3.5 text-muted-foreground" />
                                    )}
                                    <Badge variant="outline" className="text-[10px]">
                                      {row.kind === "bill" ? "Bill" : "Expense"}
                                    </Badge>
                                    {row.source === "quickbooks" && (
                                      <Badge variant="secondary" className="text-[10px]">QB</Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="font-medium font-mono text-xs">{row.ref}</TableCell>
                                <TableCell className="text-sm">{row.project}</TableCell>
                                <TableCell><StatusBadge status={row.status} /></TableCell>
                                <TableCell className="text-right font-semibold">{fmt(row.amount)}</TableCell>
                              </TableRow>
                            </CollapsibleTrigger>
                            <CollapsibleContent asChild>
                              <TableRow className="bg-muted/30 hover:bg-muted/30">
                                <TableCell colSpan={7} className="py-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs pl-8">
                                    {Object.entries(row.meta).map(([k, v]) => (
                                      <div key={k} className="flex gap-2">
                                        <span className="text-muted-foreground">{k}:</span>
                                        <span className="font-medium">{String(v ?? "—")}</span>
                                      </div>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            </CollapsibleContent>
                          </>
                        </Collapsible>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {vendor.is_1099_eligible && (
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm font-medium">1099 YTD: {fmt(stats.totalOutlays)}</p>
                      <div className="w-full bg-muted rounded-full h-2 mt-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${Math.min(100, (stats.totalOutlays / 600) * 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        ${stats.totalOutlays.toLocaleString()} / $600 threshold — 1099 {stats.totalOutlays >= 600 ? "Required ✅" : "Not yet required"}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            );
          })()}
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="space-y-4">
          <div className="flex gap-2">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note…"
              rows={2}
              className="flex-1"
            />
            <Button onClick={handleAddNote} disabled={createNote.isPending || !noteText.trim()}>
              {createNote.isPending ? "Adding…" : "Add Note"}
            </Button>
          </div>
          <div className="space-y-3">
            {notes?.map((n) => (
              <Card key={n.id}>
                <CardContent className="p-3">
                  <p className="text-sm">{n.content}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {n.created_at ? format(new Date(n.created_at), "MMM d, yyyy 'at' h:mm a") : "—"}
                  </p>
                </CardContent>
              </Card>
            ))}
            {!notes?.length && <p className="text-sm text-muted-foreground">No notes yet.</p>}
          </div>
        </TabsContent>

        {/* COMPLIANCE */}
        <TabsContent value="compliance">
          <WorkerComplianceTab
            vendorId={vendor.id}
            vendorName={vendor.name}
            companyId={vendor.company_id}
            w9OnFile={vendor.w9_on_file}
            w9Date={vendor.w9_received_date}
            agreementSigned={vendor.compliance_approved_at}
            is1099Eligible={vendor.is_1099_eligible}
            paidYTD={stats.totalOutlays}
          />
        </TabsContent>
      </Tabs>

      <NewVendorDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editVendor={vendor}
      />
    </div>
  );
};

export default VendorDetail;
